import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Platform } from '../ridango.models';

interface InertSnapshot {
    element: HTMLElement;
    restoreInert: boolean;
    previousAriaHidden: string | null;
    restoreAriaHidden: boolean;
}

type InertElement = HTMLElement & { inert: boolean };

/**
 * Global accessibility focus interceptor for Ionic modals.
 *
 * Goal:
 * Keep focus inside the top-most modal on native mobile (Android/iOS) without forcing focus
 * to any "first" input/button.
 *
 * Approach:
 * - mark everything outside the top modal as inert
 * - if focus still leaks outside, immediately blur leaked target
 *
 * TODO: when Ionic provides a built-in solution for this, we can remove this service and
 * update modals to use the native approach. See https://ionicframework.com/docs/api/modal#focustrap.
 * Currently we are on ionic 6.0.1 which does not have this feature, but this feature was released in Ionic 8.1.
 * Jira ticket: https://int.jira.ridango.com/browse/STW-897
 */
@Injectable({ providedIn: 'root' })
export class ModalFocusInterceptorService implements OnDestroy {
    private isInitialized = false;
    private isRestoringFocus = false;
    private isInertUpdateScheduled = false;
    private modalObserver: MutationObserver | null = null;
    private readonly inertSnapshots: InertSnapshot[] = [];
    private readonly lastFocusedByModal = new WeakMap<HTMLElement, HTMLElement>();

    private readonly onDidPresent = (): void => {
        const topModal = this.getTopModal();
        if (topModal && this.isSheetModal(topModal)) {
            this.moveSheetModalBeforeFooter(topModal);
        }
        this.updateOutsideInertState();

        if (topModal) {
            this.restoreFocusIntoModal(topModal);
        }
    };

    private moveSheetModalBeforeFooter(modal: HTMLElement): void {
        const parent = modal.parentElement;
        if (!parent) {
            return;
        }

        const footer = parent.querySelector('ion-footer');

        if (footer) {
            parent.insertBefore(modal, footer);
        }
    }

    private readonly onDidDismiss = (): void => {
        this.updateOutsideInertState();
    };

    private readonly onModalDomMutation = (mutations: MutationRecord[]): void => {
        for (const mutation of mutations) {
            if (this.containsModalNode(mutation.addedNodes) || this.containsModalNode(mutation.removedNodes)) {
                this.scheduleInertStateUpdate();
                return;
            }
        }
    };

    private readonly onFocusIn = (event: FocusEvent): void => {
        if (this.isRestoringFocus) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const topModal = this.getTopModal();
        if (!topModal) {
            this.clearInertState();
            return;
        }

        if (topModal.contains(target) || target === topModal) {
            if (target !== topModal) {
                this.lastFocusedByModal.set(topModal, target);
            }
            return;
        }

        // Sheet modals don't cover the whole screen and intentionally keep the app footer
        // (tab bar) interactive, so focus is allowed to stay there instead of being pulled back.
        if (this.isSheetModal(topModal)) {
            return;
        }

        // Focus leaked outside top modal (often to parent modal). Restore it back.
        event.preventDefault();
        event.stopPropagation();
        this.restoreFocusIntoModal(topModal);
    };

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly ngZone: NgZone
    ) {}

    init(): void {
        if (this.isInitialized || !this.shouldRun()) {
            return;
        }

        this.isInitialized = true;
        this.ngZone.runOutsideAngular(() => {
            this.document.addEventListener('didPresent', this.onDidPresent, true);
            this.document.addEventListener('didDismiss', this.onDidDismiss, true);
            this.document.addEventListener('ionModalDidPresent', this.onDidPresent, true);
            this.document.addEventListener('ionModalDidDismiss', this.onDidDismiss, true);
            this.document.addEventListener('focusin', this.onFocusIn, true);

            this.modalObserver = new MutationObserver(this.onModalDomMutation);
            this.modalObserver.observe(this.document.body, { childList: true, subtree: true });
        });
    }

    ngOnDestroy(): void {
        if (!this.isInitialized) {
            return;
        }

        this.document.removeEventListener('didPresent', this.onDidPresent, true);
        this.document.removeEventListener('didDismiss', this.onDidDismiss, true);
        this.document.removeEventListener('ionModalDidPresent', this.onDidPresent, true);
        this.document.removeEventListener('ionModalDidDismiss', this.onDidDismiss, true);
        this.document.removeEventListener('focusin', this.onFocusIn, true);
        this.modalObserver?.disconnect();
        this.modalObserver = null;
        this.clearInertState();
        this.isInitialized = false;
    }

    private shouldRun(): boolean {
        if (!Capacitor.isNativePlatform()) {
            return false;
        }

        const platform = Capacitor.getPlatform();
        return platform === Platform.ANDROID || platform === Platform.IOS;
    }

    private getTopModal(): HTMLElement | null {
        const modals = Array.from(this.document.querySelectorAll<HTMLElement>('ion-modal'));
        if (!modals.length) {
            return null;
        }

        for (let i = modals.length - 1; i >= 0; i -= 1) {
            const modal = modals[i];
            if (!modal.isConnected) {
                continue;
            }

            const style = this.document.defaultView?.getComputedStyle(modal);
            if (style?.display === 'none' || style?.visibility === 'hidden') {
                continue;
            }

            return modal;
        }

        return null;
    }

    private updateOutsideInertState(): void {
        this.clearInertState();

        const topModal = this.getTopModal();
        if (!topModal) {
            return;
        }

        const processed = new Set<HTMLElement>();

        this.inertOtherModals(topModal, processed);
        this.inertOutsideBodyChildren(topModal, processed);
    }

    private inertOtherModals(topModal: HTMLElement, processed: Set<HTMLElement>): void {
        const allModals = Array.from(this.document.querySelectorAll<HTMLElement>('ion-modal'));

        for (const modal of allModals) {
            if (modal !== topModal) {
                this.applyInert(modal, processed);
            }
        }
    }

    private shouldSkipInertElement(element: HTMLElement, topModal: HTMLElement): boolean {
        return (
            element === topModal ||
            element.contains(topModal) ||
            element.classList?.contains('cdk-overlay-pane') ||
            element.classList?.contains('cdk-overlay-container')
        );
    }

    private inertOutsideBodyChildren(topModal: HTMLElement, processed: Set<HTMLElement>): void {
        const bodyChildren = Array.from(this.document.body.children) as HTMLElement[];

        for (const element of bodyChildren) {
            if (this.shouldSkipInertElement(element, topModal)) {
                continue;
            }

            this.applyInert(element, processed);
        }
    }

    private applyInert(element: HTMLElement, processed: Set<HTMLElement>): void {
        if (processed.has(element)) {
            return;
        }

        processed.add(element);

        const wasInert = this.getInert(element);
        const previousAriaHidden = element.getAttribute('aria-hidden');

        const restoreInert = !wasInert;
        const restoreAriaHidden = previousAriaHidden !== 'true';

        if (restoreInert) {
            this.setInert(element, true);
        }

        if (restoreAriaHidden) {
            element.setAttribute('aria-hidden', 'true');
        }

        if (restoreInert || restoreAriaHidden) {
            this.inertSnapshots.push({
                element,
                restoreInert,
                previousAriaHidden,
                restoreAriaHidden
            });
        }
    }

    private clearInertState(): void {
        for (const snapshot of this.inertSnapshots) {
            if (snapshot.restoreInert) {
                this.setInert(snapshot.element, false);
            }
            if (snapshot.restoreAriaHidden) {
                this.restoreAriaHidden(snapshot.element, snapshot.previousAriaHidden);
            }
        }

        this.inertSnapshots.length = 0;
    }

    private scheduleInertStateUpdate(): void {
        if (this.isInertUpdateScheduled) {
            return;
        }

        this.isInertUpdateScheduled = true;
        requestAnimationFrame(() => {
            this.isInertUpdateScheduled = false;
            this.updateOutsideInertState();
        });
    }

    private containsModalNode(nodes: NodeList): boolean {
        for (const node of Array.from(nodes)) {
            if (!(node instanceof HTMLElement)) {
                continue;
            }

            if (node.tagName === 'ION-MODAL' || node.querySelector('ion-modal')) {
                return true;
            }
        }

        return false;
    }

    private restoreFocusIntoModal(modal: HTMLElement): void {
        const active = this.document.activeElement as HTMLElement | null;
        if (active && active !== modal && modal.contains(active)) {
            this.lastFocusedByModal.set(modal, active);
            return;
        }

        const remembered = this.lastFocusedByModal.get(modal);
        const target = remembered && modal.contains(remembered) ? remembered : this.getModalFocusFallback(modal);
        if (!target) {
            return;
        }

        this.isRestoringFocus = true;
        target.focus({ preventScroll: true });
        this.isRestoringFocus = false;
    }

    private getModalFocusFallback(modal: HTMLElement): HTMLElement | null {
        const wrapper = modal.shadowRoot?.querySelector<HTMLElement>('.modal-wrapper');
        if (wrapper) {
            if (!wrapper.hasAttribute('tabindex')) {
                wrapper.setAttribute('tabindex', '-1');
            }
            return wrapper;
        }

        if (!modal.hasAttribute('tabindex')) {
            modal.setAttribute('tabindex', '-1');
        }
        return modal;
    }

    private restoreAriaHidden(element: HTMLElement, previousAriaHidden: string | null): void {
        if (previousAriaHidden === null) {
            element.removeAttribute('aria-hidden');
            return;
        }

        element.setAttribute('aria-hidden', previousAriaHidden);
    }

    private isSheetModal(modal: HTMLElement | null): boolean {
        return Boolean(modal?.classList?.contains('sheet-modal'));
    }

    private isInsideAppFooter(target: HTMLElement): boolean {
        return Boolean(target.closest('ion-footer'));
    }

    private getInert(element: HTMLElement): boolean {
        return Boolean((element as InertElement).inert);
    }

    private setInert(element: HTMLElement, value: boolean): void {
        (element as InertElement).inert = value;
    }
}
